<script setup lang="ts">
import { ref, computed } from "vue";
import dayjs from "dayjs";
import "dayjs/locale/nl";
import { useApiClient } from "@/api/client";
import { toastApiResult } from "@/toaster";
import FlightSummaryDTO = Modules.CampaignPlanner.Data.FlightSummaryDTO;
import FlightSegmentSummaryDO = Modules.CampaignPlanner.Data.FlightSegmentSummaryDO;
import FlightDO = Modules.CampaignPlanner.Data.FlightDO;
import useDialog from "@/ui/composables/useDialog";
import { useRouter } from "vue-router";
import OverwritePricingDialog from "./OverwritePricingDialog.vue";
import CloneFlightDialog from "./CloneFlightDialog.vue";

const router = useRouter();

const props = defineProps<{
	flights: FlightSummaryDTO[];
	loading: boolean;
}>();

const emit = defineEmits<{
	"flight-deleted": [];
}>();

const flightHeaders = [
	{ title: "Flight Name", key: "name", align: "start", sortable: true },
	{ title: "From", key: "start_date", align: "start", sortable: true },
	{ title: "To", key: "end_date", align: "start", sortable: true },
	{ title: "Contacts", key: "total_reach", align: "end", sortable: true },
	{ title: "Price", key: "total_price", align: "end", sortable: true },
	{ title: "CPM", key: "cpm", align: "end", sortable: true },
	{
		title: "Segments",
		key: "segments_count",
		align: "center",
		sortable: false,
	},
	{ title: "Sync", key: "is_synchronized", align: "center", sortable: false },
	{
		title: "Actions",
		key: "actions",
		align: "end",
		sortable: false,
		width: 200,
	},
] as const;

const segmentHeaders = [
	{ title: "From", key: "start_date", align: "start" },
	{ title: "To", key: "end_date", align: "start" },
	{ title: "Contacts", key: "total_reach", align: "end" },
	{ title: "Price", key: "total_price", align: "end" },
	{ title: "CPM", key: "cpm", align: "end" },
	{ title: "Sync", key: "is_synchronized", align: "center" },
] as const;

const totalReach = computed(() =>
	props.flights.reduce((sum, f) => sum + f.total_reach, 0)
);

const totalPrice = computed(() =>
	props.flights.reduce((sum, f) => sum + f.total_price, 0)
);

// Contacts-weighted average of each flight's own CPM (price is not used here):
// Σ(reach × cpm) / Σ reach.
const averageCpm = computed(() => {
	const weightedCpm = props.flights.reduce(
		(sum, f) => sum + f.cpm * f.total_reach,
		0
	);
	return totalReach.value > 0 ? weightedCpm / totalReach.value : 0;
});

const formatNumber = (value: number) =>
	new Intl.NumberFormat("nl-BE").format(Math.round(value));

const formatCurrency = (value: number) =>
	new Intl.NumberFormat("nl-BE", {
		style: "currency",
		currency: "EUR",
		minimumFractionDigits: 2,
	}).format(value);

const formatDate = (value: string | null) => {
	if (!value) return "-";
	return dayjs(value).locale("nl").format("DD MMM");
};

const editingFlightUuid = ref<string | null>(null);
const editingName = ref("");

const startEdit = (flight: FlightSummaryDTO) => {
	editingFlightUuid.value = flight.uuid;
	editingName.value = flight.name ?? "";
};

const cancelEdit = () => {
	editingFlightUuid.value = null;
	editingName.value = "";
};

const saveFlightName = async (flight: FlightSummaryDTO) => {
	if (editingFlightUuid.value !== flight.uuid) return;
	const newName = editingName.value.trim();
	cancelEdit();
	if (newName === (flight.name ?? "")) return;

	const response = await toastApiResult(
		useApiClient("campaignPlanner").updateFlight(flight.uuid, { name: newName })
	);
	if (response.isOk()) flight.name = newName;
};

const segmentsByFlight = ref<Record<string, FlightSegmentSummaryDO[]>>({});
const loadingSegments = ref<Record<string, boolean>>({});

const onExpand = async (expanded: string[]) => {
	const toLoad = expanded.filter((uuid) => !segmentsByFlight.value[uuid]);
	for (const uuid of toLoad) {
		loadingSegments.value[uuid] = true;
		const response = await useApiClient("campaignPlanner").getFlightSegments(
			uuid
		);
		if (response.isOk()) segmentsByFlight.value[uuid] = response.value;
		loadingSegments.value[uuid] = false;
	}
};

const deleteFlight = async (flight: FlightSummaryDTO) => {
	const confirmed = await useDialog().confirm({
		title: "Delete Flight",
		message: `Are you sure you want to delete "${
			flight.name ?? "this flight"
		}"? This action cannot be undone.`,
	});

	if (!confirmed) return;

	const response = await toastApiResult(
		useApiClient("campaignPlanner").deleteFlight(flight.uuid)
	);

	if (response.isOk()) emit("flight-deleted");
};

const editFlight = async (
	flight: FlightDO,
	isConfirmRequired: boolean = true
) => {
	if (isConfirmRequired) {
		const confirmed = await useDialog().confirm({
			title: "Edit Flight",
			message: `You are about to modify an existing flight. Please note that this action will likely overwrite existing price quotes for this flight`,
		});

		if (!confirmed) return;
	}

	router.push({
		name: "flight.detail",
		params: {
			flight_uuid: flight.uuid,
		},
	});
};

const flightHasPqaResult = (item: FlightSummaryDTO) => {
	return item.segments_count === 0 || item.total_reach === 0;
};

const pricingOverwriteFlight = ref<FlightSummaryDTO | null>(null);
const showPricingOverwriteDialog = ref(false);

const openPricingOverwrite = (item: FlightSummaryDTO) => {
	pricingOverwriteFlight.value = item;
	showPricingOverwriteDialog.value = true;
};

const onPricingOverwriteUpdated = (updated: FlightSummaryDTO) => {
	const idx = props.flights.findIndex((f) => f.uuid === updated.uuid);
	if (idx !== -1) Object.assign(props.flights[idx], updated);
};

const cloneSourceFlight = ref<FlightSummaryDTO | null>(null);
const showCloneDialog = ref(false);

const openCloneDialog = (item: FlightSummaryDTO) => {
	cloneSourceFlight.value = item;
	showCloneDialog.value = true;
};

const onCloned = (flightDO: FlightDO) => {
	router.push({
		name: "flight.detail.process.segments",
		params: {
			flight_uuid: flightDO.uuid,
		},
	});
};

const openFlightRefineView = (item: FlightSummaryDTO) => {
	router.push({
		name: "flight.detail.refine.segments",
		params: {
			flight_uuid: item.uuid,
		},
	});
};
</script>

<template>
	<v-card elevation="1">
		<v-data-table
			:headers="flightHeaders"
			:items="flights"
			:loading="loading"
			class="elevation-0"
			:items-per-page="-1"
			hide-default-footer
			show-expand
			item-value="uuid"
			@update:expanded="onExpand"
		>
			<template v-slot:item.name="{ item }">
				<div class="d-flex align-center gap-2">
					<v-text-field
						v-if="editingFlightUuid === item.uuid"
						v-model="editingName"
						density="compact"
						variant="plain"
						hide-details
						autofocus
						class="flight-name-input"
						@blur="saveFlightName(item)"
						@keydown.enter.prevent="saveFlightName(item)"
						@keydown.escape="cancelEdit"
					/>
					<v-tooltip
						v-else
						:text="item.name ?? ''"
						location="top"
						:disabled="!item.name"
					>
						<template v-slot:activator="{ props }">
							<span
								class="flight-name font-weight-medium flight-name-editable"
								v-bind="props"
								@click="startEdit(item)"
							>
								{{ item.name ?? "—" }}
							</span>
						</template>
					</v-tooltip>
				</div>
			</template>

			<template v-slot:item.start_date="{ item }">
				<span class="font-weight-medium">{{
					formatDate(item.start_date)
				}}</span>
			</template>

			<template v-slot:item.end_date="{ item }">
				<span class="font-weight-medium">{{ formatDate(item.end_date) }}</span>
			</template>

			<template v-slot:item.total_reach="{ item }">
				{{ item.total_reach > 0 ? formatNumber(item.total_reach) : "0" }}
			</template>

			<template v-slot:item.total_price="{ item }">
				{{ formatCurrency(item.total_price)
				}}{{ item.cost_overwrite !== null ? " *" : "" }}
			</template>

			<template v-slot:item.production_cost="{ item }">
				{{
					item.production_cost > 0 ? formatCurrency(item.production_cost) : "—"
				}}
			</template>

			<template v-slot:item.cpm="{ item }">
				{{ formatCurrency(item.cpm)
				}}{{ item.cpm_overwrite !== null ? " *" : "" }}
			</template>

			<template v-slot:item.is_synchronized="{ item }">
				<v-tooltip
					v-if="item.is_synchronized"
					:text="
						item.synchronized_at ? formatDate(item.synchronized_at) : 'Synced'
					"
					location="top"
				>
					<template v-slot:activator="{ props }">
						<v-icon color="success" size="small" v-bind="props">
							fa: fa-solid fa-cloud-arrow-up
						</v-icon>
					</template>
				</v-tooltip>
				<v-icon v-else color="warning" size="small">
					fa: fa-solid fa-cloud-arrow-up
				</v-icon>
			</template>

			<template v-slot:item.segments_count="{ item }">
				<v-chip
					size="small"
					:color="item.segments_count > 0 ? 'primary' : undefined"
					variant="tonal"
				>
					{{ item.segments_count }}
				</v-chip>
			</template>

			<template v-slot:item.actions="{ item }">
				<v-btn
					size="x-small"
					icon="fa: fa-regular fa-pen"
					color="primary"
					variant="text"
					@click="editFlight(item, true)"
				/>
				<v-btn
					:disabled="flightHasPqaResult(item)"
					size="x-small"
					icon="fa: fa-regular fa-list-check"
					color="primary"
					variant="text"
					@click="openFlightRefineView(item)"
				/>
				<v-btn
					size="x-small"
					icon="fa: fa-duotone fa-split"
					color="primary"
					variant="text"
					@click="openCloneDialog(item)"
				/>
				<v-btn
					:disabled="flightHasPqaResult(item)"
					size="x-small"
					icon="fa: fa-regular fa-circle-euro"
					:color="
						item.cost_overwrite !== null || item.cpm_overwrite !== null
							? 'success'
							: 'primary'
					"
					variant="text"
					@click="openPricingOverwrite(item)"
				/>
				<v-btn
					size="x-small"
					icon="fa: fa-regular fa-trash"
					color="error"
					variant="text"
					@click="deleteFlight(item)"
				/>
			</template>

			<template v-slot:expanded-row="{ columns, item }">
				<tr>
					<td :colspan="columns.length" class="pa-0">
						<div class="segment-panel pa-3">
							<div
								v-if="loadingSegments[item.uuid]"
								class="d-flex align-center justify-center py-4 gap-2 text-medium-emphasis text-caption"
							>
								<v-progress-linear size="16" width="2" indeterminate />
								Loading segments…
							</div>

							<v-data-table
								v-else
								:headers="segmentHeaders"
								:items="segmentsByFlight[item.uuid] ?? []"
								:items-per-page="-1"
								hide-default-footer
								density="compact"
								class="segment-table elevation-0"
							>
								<template v-slot:item.start_date="{ item: seg }">
									{{ formatDate(seg.start_date) }}
								</template>

								<template v-slot:item.end_date="{ item: seg }">
									{{ formatDate(seg.end_date) }}
								</template>

								<template v-slot:item.total_reach="{ item: seg }">
									{{
										seg.total_reach > 0 ? formatNumber(seg.total_reach) : "0"
									}}
								</template>

								<template v-slot:item.total_price="{ item: seg }">
									{{ formatCurrency(seg.total_price) }}
								</template>

								<template v-slot:item.cpm="{ item: seg }">
									{{ formatCurrency(seg.cpm) }}
								</template>

								<template v-slot:item.is_synchronized="{ item: seg }">
									<v-tooltip
										v-if="seg.is_synchronized"
										:text="
											seg.synchronized_at
												? formatDate(seg.synchronized_at)
												: 'Synced'
										"
										location="top"
									>
										<template v-slot:activator="{ props }">
											<v-icon color="success" size="x-small" v-bind="props">
												fa: fa-solid fa-cloud-arrow-up
											</v-icon>
										</template>
									</v-tooltip>
									<v-icon v-else color="warning" size="x-small">
										fa: fa-solid fa-cloud-arrow-up
									</v-icon>
								</template>

								<template v-slot:no-data>
									<div
										class="py-4 text-center text-medium-emphasis text-caption"
									>
										No segments
									</div>
								</template>
							</v-data-table>
						</div>
					</td>
				</tr>
			</template>

			<template v-slot:no-data>
				<div class="py-8 text-center text-medium-emphasis">No flights yet</div>
			</template>
		</v-data-table>
	</v-card>

	<div class="kpi-strip mt-4 mt-5">
		<div class="kpi-item">
			<span class="kpi-label">Total Contacts</span>
			<span class="kpi-value">{{ formatNumber(totalReach) }}</span>
		</div>
		<div class="kpi-sep" />
		<div class="kpi-item">
			<span class="kpi-label">Total Price</span>
			<span class="kpi-value">{{ formatCurrency(totalPrice) }}</span>
		</div>
		<div class="kpi-sep" />
		<div class="kpi-item">
			<span class="kpi-label">Average CPM</span>
			<span class="kpi-value">{{ formatCurrency(averageCpm) }}</span>
		</div>
	</div>

	<overwrite-pricing-dialog
		v-if="pricingOverwriteFlight && showPricingOverwriteDialog"
		v-model="showPricingOverwriteDialog"
		:flight="pricingOverwriteFlight"
		@updated="onPricingOverwriteUpdated"
	/>

	<clone-flight-dialog
		v-if="cloneSourceFlight && showCloneDialog"
		v-model="showCloneDialog"
		:source-flight="cloneSourceFlight"
		@cloned="(flightDO : FlightDO) =>  onCloned(flightDO)"
	/>
</template>

<style scoped lang="scss">
.segment-panel {
	background: rgba(var(--v-theme-on-surface), 0.03);
	border-top: 1px solid rgba(var(--v-theme-on-surface), 0.08);
	border-bottom: 1px solid rgba(var(--v-theme-on-surface), 0.08);
}

.segment-table {
	background: transparent !important;
	opacity: 0.9;
	font-size: 0.8125rem;

	:deep(th) {
		color: rgba(var(--v-theme-on-surface), 0.5) !important;
		font-size: 0.75rem !important;
	}

	:deep(td) {
		color: rgba(var(--v-theme-on-surface), 0.6) !important;
	}
}

.flight-name {
	display: inline-block;
	max-width: 20ch;
	overflow: hidden;
	white-space: nowrap;
	text-overflow: ellipsis;
	vertical-align: bottom;
}

.flight-name-editable {
	cursor: text;
	border-bottom: 1px dashed transparent;
	transition: border-color 0.15s;

	&:hover {
		border-bottom-color: rgba(var(--v-theme-on-surface), 0.3);
	}
}

.flight-name-input {
	max-width: 28ch;

	:deep(input) {
		font-weight: 500;
		padding: 0;
	}
}

.kpi-strip {
	display: flex;
	align-items: center;
	justify-content: flex-end;
	gap: 26px;
}

.kpi-item {
	display: flex;
	align-items: baseline;
	gap: 9px;
}

.kpi-sep {
	width: 1px;
	height: 22px;
	background: rgba(var(--v-theme-on-surface), 0.12);
}

.kpi-label {
	font-size: 12.5px;
	color: rgba(var(--v-theme-on-surface), 0.6);
}

.kpi-value {
	font-size: 18px;
	font-weight: 700;
	letter-spacing: -0.01em;
}
</style>
